import React, { useState } from 'react';
import { researchToolsApi } from '../api';
import type { ExperimentDetail, ExperimentType } from '../types';
import '../research-tools-overlay.css';

interface Props {
  onSuccess: (exp: ExperimentDetail) => void;
  onBack: () => void;
}

const TYPE_OPTIONS: Array<{ value: ExperimentType; label: string }> = [
  { value: 'ASSOCIATIVE', label: 'Ассоциативный эксперимент' },
  {
    value: 'PEDAGOGICAL_PRETEST_POSTTEST',
    label: 'Педагогический эксперимент · предтест–посттест',
  },
  { value: 'EXPERT_EVALUATION', label: 'Экспертная оценка' },
  { value: 'SCALING_RATING', label: 'Шкалирование / рейтинг' },
  { value: 'OTHER', label: 'Другой эксперимент' },
];

/** Experiment builder — template surveyForm(experiment=true). */
export const ExperimentBuilderView: React.FC<Props> = ({ onSuccess, onBack }) => {
  const [title, setTitle] = useState('');
  const [experimentType, setExperimentType] = useState<ExperimentType>('ASSOCIATIVE');
  const [description, setDescription] = useState('');
  const [stimuliText, setStimuliText] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async (targetStatus: 'DRAFT' | 'MODERATION') => {
    if (!title.trim()) {
      alert('Укажите название эксперимента');
      return;
    }
    const lines = stimuliText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0) {
      alert('Укажите как минимум один вопрос или стимул (с новой строки)');
      return;
    }

    setSaving(true);
    try {
      const created = await researchToolsApi.createExperiment({
        title,
        experiment_type: experimentType,
        description,
        stimuli_questions: lines,
        status: targetStatus,
      });
      alert(
        targetStatus === 'DRAFT'
          ? 'Черновик эксперимента сохранён'
          : 'Эксперимент отправлен на модерацию владельцу системы',
      );
      onSuccess(created);
    } catch {
      alert('Ошибка при сохранении эксперимента');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <section className="research-tools-hero">
        <div>
          <small>Zertteu Academy · исследовательский контур</small>
          <h2>Провести эксперимент</h2>
          <p>
            Настройте исследовательский сценарий. Участники смогут пройти его по отдельной ссылке
            без регистрации после модерации.
          </p>
        </div>
        <button type="button" className="research-tools-btn" onClick={onBack}>
          ← Назад
        </button>
      </section>

      <section className="research-tools-panel">
        <header>
          <div>
            <h3>Конструктор</h3>
            <p>Вопросы и стимулы вводятся по одному в строке.</p>
          </div>
        </header>
        <div className="research-tools-panel-body">
          <div className="research-tools-form">
            <div className="research-tools-field">
              <label>Название *</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Название исследования"
              />
            </div>
            <div className="research-tools-field">
              <label>Тип *</label>
              <select
                value={experimentType}
                onChange={(e) => setExperimentType(e.target.value as ExperimentType)}
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="research-tools-field full">
              <label>Описание для участника</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Краткая инструкция, цель и условия участия"
                rows={3}
              />
            </div>
            <div className="research-tools-field full">
              <label>Вопросы / стимулы *</label>
              <textarea
                value={stimuliText}
                onChange={(e) => setStimuliText(e.target.value)}
                placeholder="Каждый вопрос — с новой строки"
                rows={6}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button
              type="button"
              className="research-tools-btn"
              disabled={saving}
              onClick={() => handleSave('DRAFT')}
            >
              Сохранить черновик
            </button>
            <button
              type="button"
              className="research-tools-btn primary"
              disabled={saving}
              onClick={() => handleSave('MODERATION')}
            >
              Отправить на модерацию
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
